/**
 * Chat API client for PWA messaging.
 *
 * Scope: server_readable channels only.
 * client_sealed (E2EE) is intentionally not exposed via public API.
 *
 * Security notes:
 * - All messages sent to server for encryption (server_readable mode)
 * - No client-side encryption keys exposed in UI
 * - CSRF token included for state-changing requests
 * - Session authentication via httpOnly cookies
 * - Audit events are emitted by the backend
 *
 * ACT-INDEEP-PRODUCTION-FEED-CSRF-SESSION-CONTRACT01 (CORRECTION01):
 *   CSRF helper is the shared `getCSRFToken` from `./auth` (which
 *   under CORRECTION01 is the synchronous cookie-only read; it does
 *   NOT lazily bootstrap). The previous private duplicate was
 *   removed because the canonical name now lives in `./auth`.
 *
 *   Note: chat.ts has NOT yet migrated to the
 *   `await ensureCSRFToken()` pattern that feed-posts uses. The
 *   defensive `GET /api/auth/csrf` bootstrap remains scoped to
 *   feed-posts in this ACT. If chat surfaces a similar production
 *   defect (cookie absent OR cookie carries a consumed token
 *   because the backend's CSRFStore is single-use), see P2-01
 *   cross-adapter backlog item.
 */

import { getCSRFToken, CSRF_HEADER_NAME } from './auth';

const API_BASE = '/api/v1/chat';

// =============================================================================
// Types
// =============================================================================

/**
 * Chat channel representation.
 *
 * CORRECTION01 (R2 fix): the channel now carries the S014 counterpart
 * projection for personal channels. The ChatHeader reads counterpart
 * directly from the channel so the header renders name/age/city on
 * the reload path (GET /api/v1/chat/channels/{id}) and on the list
 * path (GET /api/v1/chat/channels). The field is omitted for generic
 * channels.
 */
export interface ChatChannel {
  readonly id: string;
  readonly crypto_mode: 'server_readable';
  readonly created_at: string;
  readonly counterpart?: ChatCounterpartSummary;
}

/**
 * Chat message representation.
 * content is the decrypted plaintext returned by the server.
 */
export interface ChatMessage {
  readonly id: string;
  readonly channel_id: string;
  readonly sender_account_id: string;
  readonly content: string;
  readonly encryption_algorithm?: string;
  readonly created_at: string;
}

/**
 * Response when creating a channel.
 */
export interface CreateChannelResponse {
  id: string;
  crypto_mode: string;
  created_at: string;
}

/**
 * Response when listing channels.
 */
export interface ListChannelsResponse {
  channels: ChatChannel[];
  limit: number;
  offset: number;
  has_more: boolean;
}

/**
 * Response when sending a message.
 */
export interface SendMessageResponse {
  id: string;
  created_at: string;
}

/**
 * Response when listing messages.
 */
export interface ListMessagesResponse {
  messages: ChatMessage[];
  limit: number;
  offset: number;
  has_more: boolean;
}

/**
 * API error class with safe flag for user-facing vs internal errors.
 */
export class ChatError extends Error {
  constructor(
    message: string,
    public readonly safe: boolean = true
  ) {
    super(message);
    this.name = 'ChatError';
  }
}

// =============================================================================
// API Functions
// =============================================================================

/**
 * Create a new server_readable chat channel.
 * 
 * POST /api/v1/chat/channels
 * 
 * Security notes:
 * - Always creates server_readable channel (no client_sealed option)
 * - No sensitive data in request body
 * - Audit event emitted by backend
 */
export async function createChannel(): Promise<ChatChannel> {
  // ACT-INDEEP-PRODUCTION-FEED-CSRF-SESSION-CONTRACT01 (CORRECTION01):
  // chat.ts still uses the synchronous `getCSRFToken()` (cookie-only
  // read). The defensive `GET /api/auth/csrf` bootstrap was scoped
  // to feed-posts (the documented production defect).
  //
  // Known follow-up (P2-01 cross-adapter backlog item):
  //   The backend's CSRFStore is single-use. If the cookie value
  //   has already been consumed by a prior mutation in this tab or
  //   another tab, this request will surface as `csrf token invalid`
  //   and the chat adapter will see a 403. Until chat/profilePhotos/
  //   skills migrate to `await ensureCSRFToken()` + per-mutation
  //   fresh-token fetch, those adapters share the same production
  //   failure mode that this ACT repaired for feed-posts.
  const csrfToken = getCSRFToken();

  const response = await fetch(`${API_BASE}/channels`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(csrfToken ? { [CSRF_HEADER_NAME]: csrfToken } : {}),
    },
    credentials: 'include',
    body: JSON.stringify({}),
  });

  const data = await response.json();

  if (response.status === 401) {
    throw new ChatError('authentication_required', true);
  }

  if (response.status === 403) {
    throw new ChatError('not_authorized', true);
  }

  if (!response.ok) {
    const message = data?.error || 'failed to create channel';
    // 500 errors get generic message to avoid exposing internals
    throw new ChatError(
      response.status >= 500 ? 'failed to create channel' : message,
      response.status < 500
    );
  }

  return {
    id: data.id,
    crypto_mode: data.crypto_mode as 'server_readable',
    created_at: data.created_at,
  };
}

/**
 * Open the canonical direct personal chat channel.
 *
 * POST /api/v1/chat/channels/personal
 *
 * Bucket C (ACT-INDEEP-FIGMA-DIRECT-CHAT-BACKEND-SLICE): the entry seam
 * is symmetric. The actor is derived from the authenticated context; the
 * counterpart is identified by `target_profile_id`. The server resolves
 * the target profile to its canonical account, role-normalizes the
 * (sponsor, friend) pair, consults CHAT_READY, and returns the canonical
 * personal channel (creating it if absent). Both Sponsor-to-Friend and
 * Friend-to-Sponsor resolve to the same canonical channel row.
 *
 * The response carries a `counterpart` field with the S014 minimal
 * public projection (profile_id, display_name, age, city_or_region,
 * photo_url). photo_url is always null until durable multi-photo
 * storage lands (B-2).
 *
 * Security notes:
 * - Idempotent: returns HTTP 201 for a brand-new channel, 200 for existing.
 * - Policy denials return 403 with a non-leaking error.
 * - Infrastructure failures return 503 (or 500) — never collapsed to policy denial.
 * - Obsolete body fields (friend_profile_id) are rejected with HTTP 400.
 * - The backend is authoritative; client-side checks are convenience only.
 */
export async function openPersonalChat(
  targetProfileId: string
): Promise<{ channel: ChatChannel; created: boolean; counterpart: ChatCounterpartSummary }> {
  if (!targetProfileId || targetProfileId.trim().length === 0) {
    throw new ChatError('target_profile_id_required', true);
  }

  const csrfToken = getCSRFToken();

  const response = await fetch(`${API_BASE}/channels/personal`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(csrfToken ? { [CSRF_HEADER_NAME]: csrfToken } : {}),
    },
    credentials: 'include',
    body: JSON.stringify({ target_profile_id: targetProfileId }),
  });

  const data = response.status === 204 ? {} : await response.json().catch(() => ({}));

  if (response.status === 401) {
    throw new ChatError('authentication_required', true);
  }
  if (response.status === 403) {
    // Policy denial: CHAT_READY denies this pair, self-chat, pairing
    // unsupported, or target's profile lifecycle/block state.
    throw new ChatError('not_authorized', true);
  }
  if (response.status === 404) {
    throw new ChatError('target_profile_not_found', true);
  }
  if (response.status === 503 || response.status >= 500) {
    throw new ChatError('service_unavailable', false);
  }
  if (!response.ok) {
    const message = data?.error || 'failed to open personal chat';
    throw new ChatError(
      response.status >= 500 ? 'failed to open personal chat' : message,
      response.status < 500
    );
  }

  // CORRECTION01 (R3 fix): the previous code silently synthesized a
  // valid-looking empty counterpart when the server response was
  // missing the required `counterpart` field. This masked protocol
  // violations and made contract-drift bugs undetectable. We now
  // fail loudly: missing counterpart -> non-retryable ChatError.
  //
  // CORRECTION02 (R3 strictness): the SAME strict normalization is
  // used here as for `normalizeChannel()`. Missing required fields
  // inside a present object also fail loud. Single source of truth.
  //
  // CORRECTION02-CLOSURE01 (R3 photo_url reconciliation): the helper
  // treats photo_url as required+nullable. The OpenAPI schema lists
  // photo_url in `required:` (CORRECTION02-CLOSURE01). The wire MUST
  // send the field but its value may be null (canonical "no photo"
  // until B-2 lands durable storage). Sending `null` AND omitting the
  // field are now both treated as "no photo"; only a wrong type is a
  // protocol violation.
  const counterpart = normalizeCounterpart(data.counterpart);

  return {
    channel: {
      id: data.id,
      crypto_mode: (data.crypto_mode as 'server_readable') ?? 'server_readable',
      created_at: data.created_at,
      counterpart,
    },
    created: Boolean(data.created),
    counterpart,
  };
}

/**
 * Chat counterpart summary (S014 minimal projection). Server-authoritative;
 * bounded to the same fields already public via the discovery API.
 */
export interface ChatCounterpartSummary {
  readonly profile_id: string;
  readonly display_name: string;
  readonly age?: number;
  readonly city_or_region?: string;
  readonly photo_url: string | null;
}

/**
 * Normalize a raw counterpart payload into a ChatCounterpartSummary.
 *
 * CORRECTION02 (R3 strictness fix): the previous code rejected the
 * WHOLE counterpart object only when it was missing; required fields
 * inside a present object were silently fabricated as empty strings /
 * 0 / null. That masked protocol violations and re-introduced the
 * exact class of bug CORRECTION01 removed at the object level.
 *
 * This helper enforces the OpenAPI `ChatCounterpartSummary` schema:
 *   - profile_id: required, non-empty string
 *   - display_name: required, string (empty allowed per S014)
 *   - age: optional, integer in [0, 150]
 *   - city_or_region: optional, string
 *   - photo_url: required + nullable. The wire MUST send the field;
 *     value may be null (canonical "no photo" until B-2) or a
 *     non-empty string. Omission is treated as null (lenient).
 *     CORRECTION02-CLOSURE01 reconciled photo_url from optional to
 *     required+nullable so the projection shape is stable.
 *
 * Required-field absence OR type mismatch → throws ChatError
 * (non-retryable protocol violation).
 *
 * This is the SINGLE source of truth for counterpart validation;
 * both `openPersonalChat()` and `normalizeChannel()` route through it.
 */
function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}

function normalizeCounterpart(raw: unknown): ChatCounterpartSummary {
  // CORRECTION01 (R3): reject WHOLE-object absence.
  if (!isRecord(raw)) {
    throw new ChatError('counterpart_missing_in_response', false);
  }

  // Required: profile_id (non-empty string).
  if (
    typeof raw['profile_id'] !== 'string' ||
    raw['profile_id'].trim().length === 0
  ) {
    throw new ChatError('counterpart_profile_id_missing', false);
  }

  // Required: display_name (string; empty allowed per S014 — display name
  // may be the empty string in beta; absence is NOT a protocol violation).
  if (typeof raw['display_name'] !== 'string') {
    throw new ChatError('counterpart_display_name_missing', false);
  }

  // Optional: age (integer in [0, 150]).
  let age: number | undefined;
  if (raw['age'] !== undefined) {
    if (
      typeof raw['age'] !== 'number' ||
      !Number.isInteger(raw['age']) ||
      raw['age'] < 0 ||
      raw['age'] > 150
    ) {
      throw new ChatError('counterpart_age_malformed', false);
    }
    age = raw['age'];
  }

  // Optional: city_or_region (string; empty allowed).
  let city_or_region: string | undefined;
  if (raw['city_or_region'] !== undefined) {
    if (typeof raw['city_or_region'] !== 'string') {
      throw new ChatError('counterpart_city_malformed', false);
    }
    city_or_region = raw['city_or_region'];
  }

  // CORRECTION02-CLOSURE01 (R3 photo_url reconciliation):
  // photo_url is REQUIRED+NULLABLE. The wire MUST send the field,
  // but its value may be null (canonical "no photo" until B-2).
  // Treat explicit `null` AND omitted field equivalently; reject
  // only wrong types (numbers, booleans, arrays, objects).
  let photo_url: string | null;
  if (raw['photo_url'] === undefined || raw['photo_url'] === null) {
    photo_url = null;
  } else if (typeof raw['photo_url'] === 'string') {
    photo_url = raw['photo_url'].length === 0 ? null : raw['photo_url'];
  } else {
    throw new ChatError('counterpart_photo_url_malformed', false);
  }

  return {
    profile_id: raw['profile_id'].trim(),
    display_name: raw['display_name'],
    age,
    city_or_region,
    photo_url,
  };
}

/**
 * Normalize a raw channel response payload into a ChatChannel.
 *
 * CORRECTION01 (R3 fix): the previous code silently passed raw
 * `data.id` / `data.crypto_mode` etc. through the return value,
 * which exposed a contract-drift bug to a downstream ChatHeader
 * receiving a placeholder. The normalized form ensures the channel
 * shape is contracted and counterpart is propagated when present.
 *
 * CORRECTION01 (R2 fix): counterpart is part of the API contract;
 * ChatChannel.counterpart is optional (omitted for generic channels).
 *
 * CORRECTION02 (R3b strictness): counterpart validation now routes
 * through `normalizeCounterpart()`, the single strict source of
 * truth. Both `openPersonalChat()` and `normalizeChannel()` use it,
 * so direct-open and channel-reload have identical contract strictness.
 */
function normalizeChannel(raw: Record<string, unknown>): ChatChannel {
  const counterpartRaw = raw['counterpart'];
  return {
    id: String(raw['id'] ?? ''),
    crypto_mode: ((raw['crypto_mode'] as 'server_readable') ?? 'server_readable'),
    created_at: String(raw['created_at'] ?? ''),
    counterpart:
      counterpartRaw === undefined
        ? undefined
        : normalizeCounterpart(counterpartRaw),
  };
}

/**
 * @deprecated Replaced by openPersonalChat. The old body shape
 * (friend_profile_id) is no longer accepted by the server; calls
 * receive HTTP 400. Kept only so existing callers fail loudly until
 * they migrate.
 */
export async function createOrGetPersonalChannel(
  friendProfileId: string
): Promise<{ channel: ChatChannel; created: boolean }> {
  void friendProfileId;
  throw new ChatError('createOrGetPersonalChannel is deprecated; use openPersonalChat', false);
}

/**
 * List chat channels for the current user.
 *
 * GET /api/v1/chat/channels
 *
 * Returns only channels the user is authorized to access. For personal
 * channels this includes the canonical Sponsor↔Friend pair; for generic
 * channels this is the creator-only MVP behavior.
 */
export async function listChannels(
  limit: number = 50,
  offset: number = 0
): Promise<{ channels: ChatChannel[]; hasMore: boolean }> {
  const response = await fetch(
    `${API_BASE}/channels?limit=${limit}&offset=${offset}`,
    {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
    }
  );

  const data = await response.json();

  if (response.status === 401) {
    throw new ChatError('authentication_required', true);
  }

  if (!response.ok) {
    const message = data?.error || 'failed to list channels';
    throw new ChatError(
      response.status >= 500 ? 'failed to list channels' : message,
      response.status < 500
    );
  }

  return {
    channels: (data.channels || []).map((raw: Record<string, unknown>) =>
      normalizeChannel(raw)
    ),
    hasMore: data.has_more || false,
  };
}

/**
 * Get a single channel by ID.
 * 
 * GET /api/v1/chat/channels/{channelID}
 */
export async function getChannel(channelId: string): Promise<ChatChannel> {
  const response = await fetch(`${API_BASE}/channels/${channelId}`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
    },
    credentials: 'include',
  });

  const data = await response.json();

  if (response.status === 401) {
    throw new ChatError('authentication_required', true);
  }

  if (response.status === 403) {
    throw new ChatError('not_authorized', true);
  }

  if (response.status === 404) {
    throw new ChatError('channel_not_found', true);
  }

  if (!response.ok) {
    const message = data?.error || 'failed to get channel';
    throw new ChatError(
      response.status >= 500 ? 'failed to get channel' : message,
      response.status < 500
    );
  }

  return normalizeChannel(data as Record<string, unknown>);
}

/**
 * Send a message to a channel.
 * 
 * POST /api/v1/chat/channels/{channelID}/messages
 * 
 * Security notes:
 * - Plaintext content sent to server; server encrypts before storage
 * - No client-side encryption (server_readable mode)
 * - Content validation: non-empty, reasonable length
 * - Audit event emitted by backend
 * 
 * CRITICAL: Do not log message content in frontend.
 * The server handles encryption; frontend should treat content as sensitive.
 */
export async function sendMessage(
  channelId: string,
  content: string
): Promise<{ id: string; created_at: string }> {
  if (!content || content.trim().length === 0) {
    throw new ChatError('message_content_required', true);
  }

  const trimmedContent = content.trim();
  
  // Reasonable length limit (10KB should be plenty for a message)
  const MAX_CONTENT_LENGTH = 10240;
  if (trimmedContent.length > MAX_CONTENT_LENGTH) {
    throw new ChatError('message_too_long', true);
  }

  const csrfToken = getCSRFToken();

  const response = await fetch(`${API_BASE}/channels/${channelId}/messages`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(csrfToken ? { [CSRF_HEADER_NAME]: csrfToken } : {}),
    },
    credentials: 'include',
    body: JSON.stringify({ content: trimmedContent }),
  });

  const data = await response.json();

  if (response.status === 401) {
    throw new ChatError('authentication_required', true);
  }

  if (response.status === 403) {
    throw new ChatError('not_authorized', true);
  }

  if (response.status === 404) {
    throw new ChatError('channel_not_found', true);
  }

  if (!response.ok) {
    const message = data?.error || 'failed to send message';
    throw new ChatError(
      response.status >= 500 ? 'failed to send message' : message,
      response.status < 500
    );
  }

  return {
    id: data.id,
    created_at: data.created_at,
  };
}

/**
 * List messages for a channel.
 * 
 * GET /api/v1/chat/channels/{channelID}/messages
 * 
 * Returns decrypted message content from the server.
 * Messages are ordered by created_at descending (newest first).
 * 
 * Security notes:
 * - Messages contain decrypted plaintext from server
 * - Frontend should not log message content
 * - Content is used only for rendering
 */
export async function listMessages(
  channelId: string,
  limit: number = 50,
  offset: number = 0
): Promise<{ messages: ChatMessage[]; hasMore: boolean }> {
  const response = await fetch(
    `${API_BASE}/channels/${channelId}/messages?limit=${limit}&offset=${offset}`,
    {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
    }
  );

  const data = await response.json();

  if (response.status === 401) {
    throw new ChatError('authentication_required', true);
  }

  if (response.status === 403) {
    throw new ChatError('not_authorized', true);
  }

  if (response.status === 404) {
    throw new ChatError('channel_not_found', true);
  }

  if (!response.ok) {
    const message = data?.error || 'failed to list messages';
    throw new ChatError(
      response.status >= 500 ? 'failed to list messages' : message,
      response.status < 500
    );
  }

  return {
    messages: data.messages || [],
    hasMore: data.has_more || false,
  };
}